import React, { FC, Fragment, useEffect, useState } from 'react';
import { Button, Checkbox, Form } from 'antd';
import { useIntl } from 'react-intl';
import {
  CustomInputText,
  CustomOnlyNumber,
  CustomSelector,
} from 'src/components/forms/pin/CustomInputText';
import {
  ComboData,
  IAddressData,
  IAddressDataColombia,
} from 'src/shared/models';
import {
  customRegexName,
  idFormat,
  phoneRegex,
  useWindowSize,
  directionCharacters,
  ZipCodeFormatColombia,
} from 'utils';
import { IAddressFormValues } from 'src/components/forms/interfaces';

interface Props {
  address?: IAddressDataColombia;
  isEditForm?: boolean;
  isFirstAddress?: boolean;
  onCreateAddress?: (address: IAddressDataColombia) => void;
  onUpdateAddress?: (address: IAddressDataColombia) => void;
  regionList: ComboData[];
  isLoading?: boolean;
}
const AddressFormCo: FC<Props> = ({
  address,
  isEditForm,
  isFirstAddress,
  onCreateAddress,
  onUpdateAddress,
  regionList,
  isLoading = false,
}): JSX.Element => {
  const [form] = Form.useForm();
  const { isMD } = useWindowSize();
  const { formatMessage } = useIntl();
  const [addressData, setAddressData] = useState(address);

  const formValues: Partial<IAddressDataColombia> = {
    name: address?.name || '',
    surname: address?.surname || '',
    zipCode: address?.zipCode || '',
    address: address?.address || '',
    comment: address?.comment || '',
    phone: address?.phone || '',
    city: address?.city || '',
    regionName: address?.regionName || '',
    idNumber: address?.idNumber || '',
  };

  const formFields: IAddressFormValues[] = [
    {
      type: 'text',
      name: 'name',
      placeholder: 'form.field.name',
      handleChange: 'name',
      initialValue: name,
      rules: {
        pattern: customRegexName,
        message: formatMessage(
          { id: 'form.field.error-only-letters' },
          { name: formatMessage({ id: 'form.field.name' }) },
        ),
      },
      autocomplete: 'given-name',
      length: 25,
      value: addressData?.name,
    },
    {
      type: 'text',
      name: 'surname',
      placeholder: 'form.field.last-name',
      handleChange: 'surname',
      initialValue: formValues.surname,
      rules: {
        pattern: customRegexName,
        message: formatMessage(
          { id: 'form.field.error-only-letters' },
          { name: formatMessage({ id: 'form.field.last-name' }) },
        ),
      },
      autocomplete: 'family-name',
      length: 50,
      value: addressData?.surname,
    },
    {
      type: 'text',
      name: 'address',
      placeholder: 'form.field.address',
      handleChange: 'address',
      initialValue: formValues.address,
      rules: {
        pattern: directionCharacters,
        message: formatMessage({ id: 'form.field.error-restricted-error' }),
      },
      autocomplete: 'street-address',
      length: 70,
      value: addressData?.address,
    },
    {
      type: 'optionalText',
      name: 'comment',
      placeholder: 'form.field.addressOptionalInfo',
      handleChange: 'comment',
      initialValue: formValues.comment,
      rules: {
        pattern: directionCharacters,
        message: formatMessage({ id: 'form.field.error-restricted-error' }),
      },
      autocomplete: 'address-line1',
      length: 35,
    },
    {
      type: 'number',
      name: 'phone',
      placeholder: 'form.field.telephone',
      handleChange: 'phone',
      initialValue: formValues.phone,
      rules: {
        pattern: phoneRegex.CO,
        message: formatMessage({
          id: 'form.field.phone-error-number-co',
        }),
      },
      autocomplete: 'tel',
      length: 10,
      value: addressData?.phone,
    },
    {
      type: 'text',
      name: 'idNumber',
      placeholder: 'form.field.id',
      handleChange: 'idNumber',
      initialValue: formValues.idNumber,
      rules: {
        pattern: idFormat,
        message: formatMessage(
          { id: 'form.field.error-id-format' },
          { name: formatMessage({ id: 'form.field.id' }) },
        ),
      },
      length: 12,
      value: addressData?.idNumber,
    },
    {
      type: 'text',
      name: 'city',
      placeholder: 'form.field.city',
      handleChange: 'city',
      initialValue: formValues.city,
      rules: {
        pattern: customRegexName,
        message: formatMessage(
          { id: 'form.field.error-only-letters' },
          { name: formatMessage({ id: 'form.field.city' }) },
        ),
      },
      autocomplete: 'address-level2',
      length: 50,
      value: addressData?.city,
    },
    {
      type: 'regionSelector',
      name: 'regionName',
      placeholder: 'form.field.address.state-region',
      handleChange: 'regionName',
      initialValue: formValues.regionName,
      rules: {
        pattern: customRegexName,
        message: formatMessage(
          { id: 'form.field.error-only-letters' },
          { name: formatMessage({ id: 'form.field.address.state-region' }) },
        ),
      },
    },
    {
      type: 'text',
      name: 'zipCode',
      placeholder: 'form.field.zipCode',
      handleChange: 'zipCode',
      initialValue: formValues.zipCode,
      rules: {
        pattern: ZipCodeFormatColombia,
        message: formatMessage(
          { id: 'form.field.error-zipCode-format' },
          { name: formatMessage({ id: 'form.field.zipcode' }) },
        ),
      },
      autocomplete: 'postal-code',
      length: 6,
      value: addressData?.zipCode,
    },
  ];

  useEffect(() => {
    checkCanContinue();
  });

  useEffect(() => {
    form.resetFields();
    if (!addressData) return;
    form.setFieldsValue(formValues);
    setAddressData((data) => {
      return {
        ...data,
        address: formValues.address,
        comment: formValues.comment,
      };
    });
  }, []);

  useEffect(() => {
    if (!isFirstAddress) return;

    setAddressData((currentAddress) => {
      return {
        ...currentAddress,
        defaultAddress: true,
      };
    });
  }, []);

  const isDefaultAddressChecked = (): boolean => {
    if ((!isEditForm && isFirstAddress) || address?.defaultAddress) {
      return true;
    }
    return false;
  };

  const isDefaultAddressDisabled = () => {
    const editingDefaultAddress = Boolean(isEditForm && address.defaultAddress);

    if (isFirstAddress || editingDefaultAddress) return true;
    return false;
  };

  const handleSubmit = () => {
    let newAddress = { ...addressData };
    isEditForm ? onUpdateAddress(newAddress) : onCreateAddress(newAddress);
    form.resetFields();
  };

  const checkAddressForm = (addressData: IAddressDataColombia): boolean => {
    return Boolean(
      addressData?.name?.match(customRegexName) &&
        addressData?.surname?.match(customRegexName) &&
        addressData?.address &&
        addressData?.phone?.match(phoneRegex.CO) &&
        addressData?.idNumber?.match(idFormat) &&
        addressData?.city?.match(customRegexName) &&
        addressData?.regionName &&
        addressData?.zipCode?.match(ZipCodeFormatColombia),
    );
  };

  const checkCanContinue = () => {
    const isDataCorrect = checkAddressForm(addressData);
    if (addressData?.canContinue == isDataCorrect) return;
    setAddressData((data) => {
      return {
        ...data,
        canContinue: isDataCorrect,
      };
    });
  };

  const handleChange = (field: string) => (event: any) => {
    if (event.persist) event.persist();

    setAddressData((data) => {
      const newData = { ...data };
      let value = event?.target?.value;
      switch (field) {
        case 'phone':
        case 'regionName':
          value = event;
          break;
        case 'defaultAddress':
          value = event.target.checked;
          break;
        default:
          value = event.target.value;
          break;
      }

      return { ...newData, [field]: value };
    });
  };

  const renderTitle = (
    <>
      {isMD && (
        <h4 className="address__form-title">
          {isEditForm
            ? formatMessage({ id: 'form.address.edit-title' })
            : formatMessage({ id: 'form.address.new-title' })}
        </h4>
      )}
      {!isMD && !isEditForm && (
        <h4 className="address__form-title">
          {formatMessage({ id: 'form.address.new-title' })}
        </h4>
      )}
    </>
  );

  const renderFields = formFields.map((field) => {
    let component: JSX.Element;
    switch (field.type) {
      case 'text':
        component = CustomInputText(
          form,
          field.name,
          field.placeholder,
          handleChange(field.name),
          field.initialValue,
          field.length,
          true,
          [field.rules],
          null,
          field.autocomplete,
        );
        break;
      case 'optionalText':
        component = CustomInputText(
          form,
          field.name,
          field.placeholder,
          handleChange(field.name),
          field.initialValue,
          field.length,
          false,
          [field.rules],
          null,
          field.autocomplete,
        );
        break;
      case 'number':
        component = CustomOnlyNumber(
          form,
          field.name,
          field.placeholder,
          handleChange(field.name),
          field.initialValue,
          field.length ?? 40,
          true,
          [field.rules],
          null,
          field.autocomplete,
        );
        break;
      case 'regionSelector':
        component = CustomSelector(
          form,
          field.name,
          regionList,
          field.placeholder,
          'value',
          handleChange(field.name),
          false,
          'description',
        );
        break;
      default:
        return;
    }
    return <Fragment key={field.name}>{component}</Fragment>;
  });

  return (
    <>
      {renderTitle}
      <Form
        name="addressForm"
        form={form}
        className="address__form"
        onFinish={handleSubmit}>
        {renderFields}
        {!isDefaultAddressDisabled() && (
          <Form.Item
            name="defaultAddress"
            valuePropName="checked"
            initialValue={isDefaultAddressChecked()}>
            <Checkbox
              className="address__form__button-wrapper"
              defaultChecked={isDefaultAddressChecked()}
              onChange={handleChange('defaultAddress')}
              disabled={isDefaultAddressDisabled()}>
              {formatMessage({ id: 'form.field.address.preferred' })}
            </Checkbox>
          </Form.Item>
        )}
        <Form.Item shouldUpdate className="address__form__button-wrapper">
          {() => (
            <Button
              htmlType="submit"
              className="address__form__button-submit btn-primary btn-primary--black"
              disabled={!addressData?.canContinue || isLoading}>
              {formatMessage({ id: 'form.address.add-new' })}
              {isMD && (
                <i className="icon icon--arrow-normal-right-white button-icon" />
              )}
            </Button>
          )}
        </Form.Item>
      </Form>
    </>
  );
};

export default AddressFormCo;
